import { z } from 'zod'
import { NO_COUPON } from '~/constants/cart'
import { CartSchema } from '~/schemas/cart/cart'
import { CouponSchema } from '~/schemas/cart/coupon'
import { AddressSchema } from '~/schemas/customer/address'
import { CustomerSchema, UpdateCustomerRequestSchema } from '~/schemas/customer/customer'
import { OrderPaymentTransactionSchema } from '~/schemas/customer/order'
import { MoneySchema } from '~/schemas/shared/money'
import { OptionalShippingBySellerSchema } from '~/schemas/shared/shipping'

/**
 * Schemas do checkout (tela /checkout): o pedido em andamento, as formas de pagamento e o cupom.
 *
 * Contrato da API (Glofi.API/Controllers/CheckoutController.cs), travado em
 * tests/Glofi.API.Tests/Snapshots/Checkout/{create,remove-coupon}.shape.txt. Todas as rotas abaixo exigem login e
 * devolvem o mesmo corpo (`ICheckout`):
 *
 * | Rota | Verbo | Corpo | Resposta 200 |
 * | --- | --- | --- | --- |
 * | `checkout/create` | POST | `{ coupon }` | `ICheckout` (cria ou reaproveita o pedido InProgress do carrinho) |
 * | `checkout/update-customer` | PUT | `IUpdateCheckoutCustomerRequest` | `ICheckout` |
 * | `checkout/create-address` | POST | `ICreateAddressRequest` (o mesmo do perfil) | `ICheckout` |
 * | `checkout/change-address` | PUT | `{ addressId }` | `ICheckout` |
 * | `checkout/apply-coupon` | POST | `{ coupon }` | `ICheckout` |
 * | `checkout/remove-coupon` | POST (GET legado) | - | `ICheckout` |
 * | `checkout/select-shipping` | PUT | `{ sellerId, offerCode }` | `ICheckout` (só na staging/api; 422 com `message` se a opção saiu da cotação) |
 *
 * Erros (`statusError` no corpo): 409 `cartNotFound` (carrinho virou pedido ou expirou), 409 `outStockError` com
 * `stockChanges`, 409 `orderError` (sem pedido em andamento; no create, também o carrinho que já virou pedido, com
 * `orderNumber` e `status`), 400 `customerError` (cadastro incompleto, com `errors`), 400 `addressError`,
 * `noAddressError` e `notFoundZipCodeError` (endereço de entrega), 400 `cartError` (carrinho vazio, cupom inválido).
 *
 * O carrinho do pedido (`order.cart`) é o `CartSchema` e o cupom é o `CouponSchema`, ambos do domínio cart: a API
 * devolve o mesmo CartDTO do `cart/current` (outra projeção do produto) e o mesmo DiscountCodeDTO do `discountcode/*`.
 *
 * Frete: a API calcula `cart.shippingTax` pelo endereço de entrega do pedido (o `freightId` dos itens é sempre nulo), e
 * trocar o endereço recalcula. Na staging/api, as respostas trazem também `shippingBySeller` (frete por vendedor) e o
 * cliente escolhe a opção de cada vendedor pelo `checkout/select-shipping`; na production/api o campo não vem (`null`).
 */

/** Texto que a API pode mandar nulo ou omitir. */
const nullableText = z.string().nullish()

// ===== ERROS =====

/** `statusError` do checkout que a tela trata como fluxo (abrir modal, voltar ao carrinho), e não como aviso. */
export const CHECKOUT_STATUS_ERROR = {
    CART_NOT_FOUND: 'cartNotFound',
    CART: 'cartError',
    ORDER: 'orderError',
    OUT_OF_STOCK: 'outStockError',
    CUSTOMER: 'customerError',
    ADDRESS: 'addressError',
    NO_ADDRESS: 'noAddressError',
    ZIP_CODE_NOT_FOUND: 'notFoundZipCodeError',
    ZIP_CODE: 'zipCodeError',
    /** Marcado pelo service do pagamento na recusa do finish (a API responde 400 sem `statusError`). */
    PAYMENT_DECLINED: 'paymentDeclined',
} as const

export type ICheckoutStatusError = (typeof CHECKOUT_STATUS_ERROR)[keyof typeof CHECKOUT_STATUS_ERROR]

/** 409 `orderError` do create quando o carrinho já virou pedido (pago, aguardando ou em pagamento). */
export const OrderConflictBodySchema = z.object({
    statusError: z.literal(CHECKOUT_STATUS_ERROR.ORDER),
    message: nullableText,
    orderNumber: z.string(),
    status: nullableText,
})

/** 409 `outStockError` do create e do finish: o `OutStockErrorBodySchema` do domínio cart (fonte única). */

// ===== RESPOSTA =====

/**
 * Cupom do checkout: o `CouponSchema` do carrinho. Sem cupom aplicado a API devolve um DiscountCodeDTO vazio (código
 * nulo, valores zerados): vira `null`, para a tela não testar campo a campo.
 */
export const CheckoutDiscountCodeSchema = z.preprocess(
    (coupon) => (coupon && typeof coupon === 'object' && (coupon as { code?: unknown }).code ? coupon : null),
    CouponSchema.nullable(),
)

/** Forma de pagamento disponível: só `id` e `name` (a API não devolve o tipo; ver `getPaymentKind`). */
export const CheckoutPaymentMethodSchema = z.object({
    id: z.string(),
    name: z.string(),
})

/** Endereço de entrega copiado no pedido: o endereço do cliente (com `id`, que a tela compara) mais o país. */
export const CheckoutAddressInfoSchema = AddressSchema.extend({
    country: nullableText,
})

/** Pedido em andamento (`CheckoutService.CleanOrder`). */
export const CheckoutOrderSchema = z.object({
    orderNumber: z.string(),
    total: MoneySchema,
    shippingAddress: z.object({ addressInfo: CheckoutAddressInfoSchema.nullish() }).nullish(),
    paymentMethod: CheckoutPaymentMethodSchema.nullish(),
    paymentPolicyRule: z.object({ name: nullableText }).nullish(),
    paymentTransaction: z
        .array(OrderPaymentTransactionSchema)
        .nullish()
        .transform((value) => value ?? []),
    /** O carrinho do pedido: o `CartSchema` do domínio cart. */
    cart: CartSchema,
    /** Cliente do pedido (o mesmo formato do cliente logado). */
    customer: CustomerSchema.nullish(),
})

/** Corpo comum das rotas do checkout. */
export const CheckoutResponseSchema = z.object({
    order: CheckoutOrderSchema,
    paymentMethods: z
        .array(CheckoutPaymentMethodSchema)
        .nullish()
        .transform((value) => value ?? []),
    discountCode: CheckoutDiscountCodeSchema,
    /** Frete por vendedor (staging/api); `null` quando a API não manda. */
    shippingBySeller: OptionalShippingBySellerSchema,
})

// ===== REQUISIÇÃO =====

/** `checkout/create`. Sem cupom (ausente, vazio ou o sentinela legado) vai `null`. */
export const CreateCheckoutRequestSchema = z.object({
    coupon: z
        .string()
        .nullish()
        .transform((value) => {
            const coupon = value?.trim()
            return coupon && coupon !== NO_COUPON ? coupon : null
        }),
})

/** `checkout/apply-coupon`. */
export const CheckoutCouponRequestSchema = z.object({
    coupon: z.string().trim().min(1),
})

/** `checkout/change-address`: o endereço salvo do cliente que vira o de entrega. */
export const ChangeCheckoutAddressRequestSchema = z.object({
    addressId: z.string().min(1),
})

/**
 * `checkout/update-customer` (EditCustomerRequest): os campos do `customer/update` (PF) e, para o cliente que entrou
 * sem cadastro completo (login rápido), a senha.
 */
export const UpdateCheckoutCustomerRequestSchema = UpdateCustomerRequestSchema.extend({
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
})

// ===== TIPOS =====

export type ICheckout = z.infer<typeof CheckoutResponseSchema>
export type ICheckoutOrder = z.infer<typeof CheckoutOrderSchema>
export type ICheckoutAddressInfo = z.infer<typeof CheckoutAddressInfoSchema>
export type ICheckoutPaymentMethod = z.infer<typeof CheckoutPaymentMethodSchema>
export type ICreateCheckoutRequest = z.input<typeof CreateCheckoutRequestSchema>
export type IChangeCheckoutAddressRequest = z.infer<typeof ChangeCheckoutAddressRequestSchema>
export type IUpdateCheckoutCustomerRequest = z.input<typeof UpdateCheckoutCustomerRequestSchema>
export type IOrderConflictBody = z.infer<typeof OrderConflictBodySchema>
